import { isConnectedAccount, hasConnectedAccounts } from 'owa-accounts-store';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import getEventMetricProperties from 'owa-calendar-compose-form-store/lib/utils/getEventMetricProperties';
import logSurveyActivity from './logSurveyActivity';
import logLocationMetrics from './logLocationMetrics';
import { MetricsInfo } from 'owa-calendar-compose-types';
import {
    EventCompletionStatusType,
    QuickComposeEntrySourceType,
    FullComposeEntrySourceType,
} from 'owa-calendar-helpers-types/lib/ComposeMetricTypes';
import { differenceInMinutes } from 'owa-datetime';
import { PerformanceDatapoint } from 'owa-analytics';
import {
    ComposeFormType,
    QuickComposeViewType,
    FullComposeFormViewType,
} from 'owa-calendar-helpers-types/lib/ComposeTypes';
import shouldShowTimeSuggestions from 'owa-calendar-compose-form-store/lib/selectors/shouldShowTimeSuggestions';
import getTimeSuggestionsRequestState from 'owa-calendar-time-suggestions/lib/selectors/getTimeSuggestionsRequestState';
import logSmartReplyWithMeetingMetricProperties from './logSmartReplyWithMeetingMetricProperties';
import logReplyAllByMeetingMetricProperties from './logReplyAllByMeetingMetricProperties';
import areOriginalRecipientsIncludedInMeeting from './areOriginalRecipientsIncludedInMeeting';
import AttendeeType from 'owa-service/lib/contract/AttendeeType';
import { getUserMailboxInfo } from 'owa-client-ids';
import getSelectedCalendarsForUser from 'owa-calendar-forms-common/lib/utils/getSelectedCalendarsForUser';
import logAutoRoomBookingMetric from './logAutoRoomBookingMetric';
import logSaveFocusTimeEvent from './logSaveFocusTimeEvent';
import { lazyLogSaveTravelTimeEvent } from 'owa-mobility-common';
import { isFeatureEnabled } from 'owa-feature-flags';
import {
    getTimeSuggestionsDiffInMinutesFromEvent,
    isTimeSuggestionLastTimeChangeAction,
} from './timeSuggestionsLogsHelper';
import getDefaultShouldShortenEventDurationEndEarly from 'owa-calendar-cloud-settings/lib/utils/getDefaultShouldShortenEventDurationEndEarly';
import getDefaultShouldShortenEventDurationStartLate from 'owa-calendar-cloud-settings/lib/utils/getDefaultShouldShortenEventDurationStartLate';
import hasShownTimeSuggestions from 'owa-calendar-compose-form-store/lib/selectors/hasShownTimeSuggestions';

export default function logEventMetricProperties(
    datapoint: PerformanceDatapoint,
    diffEvent: CalendarEvent, // The event that contains only the properties that have been changed
    quickComposeDiffEvent: CalendarEvent, // The event that contains only the changes in Quick Form before user switches to Full Form
    fullComposeDiffEvent: CalendarEvent, // The event that contains only the changes in Full Form after user switched from Quick Form
    event: CalendarEvent, // The event to be saved - include all the values that are set on diffEvent
    eventCompletionType: EventCompletionStatusType,
    formType: ComposeFormType,
    viewType: QuickComposeViewType | FullComposeFormViewType,
    quickComposeEntrySource: QuickComposeEntrySourceType,
    metricsInfo: MetricsInfo,
    fullComposeEntrySource?: FullComposeEntrySourceType,
    originalEvent?: CalendarEvent // The original calendar event before editing
) {
    let isNewEvent: boolean = !event.ItemId || !event.ItemId.Id; // If Id is not set, then it is a new event scenario
    // get compose metric data and log
    let eventMetricProperties = getEventMetricProperties(
        diffEvent,
        quickComposeDiffEvent,
        fullComposeDiffEvent,
        event,
        eventCompletionType,
        formType,
        viewType,
        quickComposeEntrySource,
        metricsInfo,
        fullComposeEntrySource
    );

    if (isFeatureEnabled('cal-time-panel-focusTime') && event.IsBookedFreeBlocks) {
        logSaveFocusTimeEvent(eventMetricProperties);
    }

    if (isFeatureEnabled('cal-mf-mobility') && !isFeatureEnabled('cal-mf-mobility-create')) {
        lazyLogSaveTravelTimeEvent.importAndExecute();
    }

    datapoint.addCustomData(eventMetricProperties);

    datapoint.addCustomData({
        EventDuration: differenceInMinutes(event.End, event.Start),
        SelectedCalendarsForRecipientsCount:
            getNumberOfSelectedCalendarsForRecipients(event.RequiredAttendees) +
            getNumberOfSelectedCalendarsForRecipients(event.OptionalAttendees),
    });

    datapoint.addCustomData({ HasConnectedAccounts: hasConnectedAccounts() });
    if (isConnectedAccount(event.ParentFolderId?.mailboxInfo?.userIdentity)) {
        datapoint.addCustomData({ MailboxType: 'Connected' });
    }

    let isTimeSuggestionTaken: boolean = false;

    if (
        shouldShowTimeSuggestions() &&
        metricsInfo.timeSuggestionStartLastSet &&
        getTimeSuggestionsRequestState()
    ) {
        // Log the difference in minutes between the suggestions and the selected time for the last set of suggestions
        let diffInMinutesLastSet = getTimeSuggestionsDiffInMinutesFromEvent(
            event,
            metricsInfo.timeSuggestionStartLastSet
        );

        // Log the difference in minutes between the suggestions and the selected time for the first set of suggestions
        let diffInMinutesFirstSet = getTimeSuggestionsDiffInMinutesFromEvent(
            event,
            metricsInfo.timeSuggestionStartFirstSet
        );

        // isTimeSuggestionTaken is true when the last time changed action came from time suggestions
        isTimeSuggestionTaken =
            diffInMinutesLastSet.length > 0 && isTimeSuggestionLastTimeChangeAction();

        const hasSuggestionMatchForEventStart = diffInMinutesLastSet?.indexOf(0) > -1;

        datapoint.addCustomData({
            timeSuggestionShown: metricsInfo.timeSuggestionShown.join(','),
            timeSuggestionClickedIndex: metricsInfo.timeSuggestionClickedIndex.join(','),
            timeSuggestionNotFound: metricsInfo.timeSuggestionNotFoundCount,
            timeSuggestionSAClicked: metricsInfo.timeSuggestionSAClickedCount,
            isTimeSuggestionTaken: isTimeSuggestionTaken || false,
            diffInMinFromTimeSuggestionFirstSet: diffInMinutesFirstSet.join(','),
            diffInMinFromTimeSuggestion: diffInMinutesLastSet.join(','),
            hasSuggestionInteractions: metricsInfo.timeSuggestionInteraction,
            hasSuggestionShown: hasShownTimeSuggestions(),
            hasSuggestionMatch: hasSuggestionMatchForEventStart,
            findMeetingTimesRequestId: metricsInfo.findMeetingTimesRequestId,
        });
    }

    if (metricsInfo.changeTimeActions && metricsInfo.changeTimeActions.length > 0) {
        datapoint.addCustomData({
            changeTimeActions: metricsInfo.changeTimeActions.join(','),
        });
    }

    logSurveyActivity(eventCompletionType, isNewEvent, eventMetricProperties.IsMeeting);
    logLocationMetrics(diffEvent, metricsInfo.formSessionId, metricsInfo.locationMetrics);
    logAutoRoomBookingMetric(metricsInfo.formSessionId, metricsInfo.autoRoomBookingMetric);

    if (metricsInfo.isBusyResourceSendClicked != undefined) {
        datapoint.addCustomData({
            IsBusyResourceSendClicked: metricsInfo.isBusyResourceSendClicked,
        });
    }

    if (fullComposeEntrySource) {
        // Log meeting data that are specifically for smart time usage
        if (fullComposeEntrySource === 'Mail_SmartReplyWithMeeting') {
            const areOriginalRecipientsIncluded = areOriginalRecipientsIncludedInMeeting(
                diffEvent,
                originalEvent
            );

            logSmartReplyWithMeetingMetricProperties(
                eventMetricProperties,
                originalEvent,
                diffEvent,
                isTimeSuggestionTaken,
                areOriginalRecipientsIncluded,
                metricsInfo.extractionSourceId,
                eventCompletionType
            );
            // Log meeting data that are specifically for reply all by meeting usage
        } else if (fullComposeEntrySource === 'Mail_ReplyByAllMeeting') {
            logReplyAllByMeetingMetricProperties(diffEvent);
        }
    }

    // Log the interactions with the time picker
    // Distinguish between selecting an option and typing the time
    datapoint.addCustomData({
        StartTimeChangedCount: metricsInfo.startTimeChangedCount,
        EndTimeChangedCount: metricsInfo.endTimeChangedCount,
        StartTimeManuallyChangedCount: metricsInfo.startTimeManuallyChangedCount,
        EndTimeManuallyChangedCount: metricsInfo.endTimeManuallyChangedCount,
    });

    const speedyMeetingsScope = getDefaultShouldShortenEventDurationEndEarly()
        ? 'EndEarly'
        : getDefaultShouldShortenEventDurationStartLate()
        ? 'StartLate'
        : 'None';

    datapoint.addCustomData({
        SpeedyMeetingsScope: speedyMeetingsScope,
    });
}

function getNumberOfSelectedCalendarsForRecipients(recipients: AttendeeType[]): number {
    if (!recipients) {
        return 0;
    }

    let countSelectedCalendars = 0;
    recipients.forEach(recipient => {
        if (isCalendarSelectedForRecipient(recipient)) {
            countSelectedCalendars++;
        }
    });

    return countSelectedCalendars;
}

function isCalendarSelectedForRecipient(recipient: AttendeeType): boolean {
    if (!recipient) {
        return false;
    }

    const selectedCalendars = getSelectedCalendarsForUser(getUserMailboxInfo().userIdentity);

    return (
        selectedCalendars /* We need this null check because for new users, it is possible selectedCalendars list is not initialized yet */ &&
        selectedCalendars.filter(
            calendar =>
                (calendar.calculatedFolderId &&
                    calendar.calculatedFolderId.toLowerCase() ===
                        recipient.Mailbox.EmailAddress?.toLowerCase()) /* for people's calendars */ ||
                (calendar.id?.id &&
                    calendar.id.id.toLowerCase() ===
                        recipient.Mailbox.EmailAddress?.toLowerCase()) /* for groups calendars */
        ).length != 0
    );
}
